import { computed } from 'vue'
import type { ComputedRef } from 'vue'
import { storeToRefs } from 'pinia'

import {
  formatValue,
  formatDelta,
} from '@app/application/report/components/graphs-drawer/format-summary'
import { useGraphChartData } from '@app/application/report/composables/use-graph-chart-data'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import type { DrawerGraphEntry } from '@app/application/report/types'
import type { ReportGraph } from '@app/application/report/screens/graphs-echarts/types'
import type { MetricPillDelta } from '@app/shared/vue/components/sf-metric-pill/types'

export interface UseDrawerPillBindingsReturn {
  graph: ComputedRef<ReportGraph>
  displayValue: ComputedRef<string>
  displayDelta: ComputedRef<MetricPillDelta | undefined>
  graphData: ReturnType<typeof useGraphChartData>['graphData']
  loading: ReturnType<typeof useGraphChartData>['loading']
  errored: ReturnType<typeof useGraphChartData>['errored']
  fetchIfNeeded: ReturnType<typeof useGraphChartData>['fetchIfNeeded']
  onHighlightToggle: (next: boolean) => void
  onPinToggle: (next: boolean) => void
}

export function useDrawerPillBindings(
  entry: () => DrawerGraphEntry
): UseDrawerPillBindingsReturn {
  const store = useReportGraphsDrawer()
  const { reportTemplateId, locale } = storeToRefs(store)

  const graphIdRef = computed(() => entry().id)

  const graph = computed<ReportGraph>(() => ({
    id: entry().id,
    reportTemplateId: reportTemplateId.value,
    title: entry().summary.title,
    canEditGraph: true,
    locale: locale.value,
  }))

  const displayValue = computed(() => formatValue(entry().summary))
  const displayDelta = computed<MetricPillDelta | undefined>(() =>
    formatDelta(entry().summary)
  )

  const { graphData, loading, errored, fetchIfNeeded } = useGraphChartData(
    graphIdRef,
    reportTemplateId,
    locale
  )

  function onHighlightToggle(next: boolean): void {
    void store.setHighlighted(entry().id, next)
  }

  function onPinToggle(next: boolean): void {
    void store.setPinned(entry().id, next)
  }

  return {
    graph,
    displayValue,
    displayDelta,
    graphData,
    loading,
    errored,
    fetchIfNeeded,
    onHighlightToggle,
    onPinToggle,
  }
}
